const DEFAULT_GATEWAYS = {
    monthly: {
        price: 0,
        prices: {
            usd: 0,
            mmk: 0,
        },
        priceDisplay: {
            primaryCurrency: 'mmk',
            showSecondary: true,
        },
        durationDays: 30,
    },
    yearly: {
        price: 0,
        prices: {
            usd: 0,
            mmk: 0,
        },
        priceDisplay: {
            primaryCurrency: 'mmk',
            showSecondary: true,
        },
        durationDays: 365,
        discountAmountYearly: 0,
        discountAmounts: {
            usd: 0,
            mmk: 0,
        },
    },
};

export const PLAN_PRICE_CURRENCIES = [
    { key: 'usd', code: 'USD', label: 'USD', symbol: '$' },
    { key: 'mmk', code: 'MMK', label: 'Ks', symbol: 'Ks' },
];

export const createPlanId = (value = '') =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 64);

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const toBoolean = (value, fallback = true) => {
    if (value === undefined || value === null || value === '') return fallback;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    const normalized = String(value).trim().toLowerCase();
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    return fallback;
};

const normalizeCurrencyKey = (value = 'USD') => {
    const normalized = String(value || '').trim().toUpperCase();
    return ['MMK', 'KS', 'KYAT'].includes(normalized) ? 'mmk' : 'usd';
};

const normalizePriceDisplaySettings = (source = {}, fallback = {}, planCurrency = 'USD') => {
    const sourceDisplay = source?.priceDisplay || source?.price_display || source?.display || {};
    const fallbackDisplay = fallback?.priceDisplay || fallback?.price_display || fallback?.display || {};
    const primaryCurrency = normalizeCurrencyKey(
        sourceDisplay?.primaryCurrency
        ?? sourceDisplay?.primary_currency
        ?? sourceDisplay?.primary
        ?? source?.primaryCurrency
        ?? source?.primary_currency
        ?? fallbackDisplay?.primaryCurrency
        ?? fallbackDisplay?.primary_currency
        ?? fallbackDisplay?.primary
        ?? fallback?.primaryCurrency
        ?? fallback?.primary_currency
        ?? planCurrency
    );
    const showSecondary = toBoolean(
        sourceDisplay?.showSecondary
        ?? sourceDisplay?.show_secondary
        ?? sourceDisplay?.showSecondaryPrice
        ?? sourceDisplay?.show_secondary_price
        ?? source?.showSecondary
        ?? source?.show_secondary
        ?? fallbackDisplay?.showSecondary
        ?? fallbackDisplay?.show_secondary
        ?? fallbackDisplay?.showSecondaryPrice
        ?? fallbackDisplay?.show_secondary_price
        ?? fallback?.showSecondary
        ?? fallback?.show_secondary,
        true
    );

    return {
        primaryCurrency,
        showSecondary,
    };
};

const getNestedCurrencyValue = (source, currencyKey, fieldPrefix) => (
    source?.[fieldPrefix]?.[currencyKey]
    ?? source?.[fieldPrefix]?.[currencyKey.toUpperCase()]
    ?? source?.[`${fieldPrefix}_${currencyKey}`]
    ?? source?.[`${fieldPrefix}${currencyKey.charAt(0).toUpperCase()}${currencyKey.slice(1)}`]
    ?? (fieldPrefix === 'prices' ? source?.[`price${currencyKey.charAt(0).toUpperCase()}${currencyKey.slice(1)}`] : undefined)
    ?? (fieldPrefix === 'prices' ? source?.[`price_${currencyKey}`] : undefined)
    ?? (fieldPrefix === 'discountAmounts' ? source?.[`discountAmount${currencyKey.charAt(0).toUpperCase()}${currencyKey.slice(1)}`] : undefined)
    ?? (fieldPrefix === 'discountAmounts' ? source?.[`discount_amount_${currencyKey}`] : undefined)
);

const normalizeCurrencyPair = ({
    source = {},
    fallback = {},
    legacyValue = 0,
    legacyCurrencyKey = 'usd',
    fieldPrefix = 'prices',
} = {}) => {
    const explicitUsd = getNestedCurrencyValue(source, 'usd', fieldPrefix)
        ?? getNestedCurrencyValue(fallback, 'usd', fieldPrefix);
    const explicitMmk = getNestedCurrencyValue(source, 'mmk', fieldPrefix)
        ?? getNestedCurrencyValue(fallback, 'mmk', fieldPrefix);

    return {
        usd: toNumber(explicitUsd ?? (legacyCurrencyKey === 'usd' ? legacyValue : 0), 0),
        mmk: toNumber(explicitMmk ?? (legacyCurrencyKey === 'mmk' ? legacyValue : 0), 0),
    };
};

const normalizeGateway = (gateway = {}, fallback = {}, planCurrency = 'USD') => {
    const legacyCurrencyKey = normalizeCurrencyKey(planCurrency);
    const legacyPrice = toNumber(gateway?.price ?? fallback?.price ?? 0, 0);
    const prices = normalizeCurrencyPair({
        source: gateway,
        fallback,
        legacyValue: legacyPrice,
        legacyCurrencyKey,
        fieldPrefix: 'prices',
    });
    const legacyDiscount = toNumber(
        gateway?.discountAmountYearly ?? fallback?.discountAmountYearly ?? 0,
        0
    );
    const discountAmounts = normalizeCurrencyPair({
        source: gateway,
        fallback,
        legacyValue: legacyDiscount,
        legacyCurrencyKey,
        fieldPrefix: 'discountAmounts',
    });

    return {
        price: prices.usd || prices.mmk || 0,
        prices,
        priceUsd: prices.usd,
        priceMmk: prices.mmk,
        durationDays: toNumber(gateway?.durationDays ?? fallback?.durationDays ?? 0, 0),
        discountAmountYearly: discountAmounts.usd || discountAmounts.mmk || 0,
        discountAmounts,
        discountAmountUsd: discountAmounts.usd,
        discountAmountMmk: discountAmounts.mmk,
        priceDisplay: normalizePriceDisplaySettings(gateway, fallback, planCurrency),
    };
};

export const normalizePlanRecord = (plan = {}) => {
    const legacyMonthlyPrice = Number(plan?.priceMonthly ?? plan?.price_monthly ?? plan?.price ?? 0) || 0;
    const legacyYearlyPrice = Number(plan?.priceYearly ?? plan?.price_yearly ?? legacyMonthlyPrice * 12) || 0;
    const legacyDuration = Number(plan?.durationDays ?? plan?.duration_days ?? 30) || 30;
    const planCurrency = plan?.currency || 'USD';

    const monthly = normalizeGateway(plan?.gateways?.monthly, {
        ...DEFAULT_GATEWAYS.monthly,
        price: legacyMonthlyPrice,
        durationDays: legacyDuration,
    }, planCurrency);
    const yearly = normalizeGateway(plan?.gateways?.yearly, {
        ...DEFAULT_GATEWAYS.yearly,
        price: legacyYearlyPrice,
    }, planCurrency);

    return {
        ...plan,
        currency: planCurrency,
        isPopular: plan?.isPopular ?? plan?.is_popular ?? false,
        displayOrder: plan?.displayOrder ?? plan?.display_order ?? 0,
        gateways: {
            monthly,
            yearly,
        },
    };
};

export const normalizePlanRecords = (plans = []) => plans.map(normalizePlanRecord);

export const getGatewayPrice = (gateway = {}, currencyKey = 'usd') => {
    const normalizedCurrencyKey = normalizeCurrencyKey(currencyKey);
    const value = gateway?.prices?.[normalizedCurrencyKey]
        ?? gateway?.prices?.[normalizedCurrencyKey.toUpperCase()]
        ?? gateway?.[`price${normalizedCurrencyKey.charAt(0).toUpperCase()}${normalizedCurrencyKey.slice(1)}`]
        ?? gateway?.[`price_${normalizedCurrencyKey}`];

    if (value !== undefined && value !== null && value !== '') {
        return toNumber(value, 0);
    }

    if (normalizedCurrencyKey === 'usd') {
        return toNumber(gateway?.price, 0);
    }

    return 0;
};

export const getGatewayDurationDays = (gateway = {}) => toNumber(gateway?.durationDays, 0);

export const getGatewayDiscountAmount = (gateway = {}, currencyKey = 'usd') => {
    const normalizedCurrencyKey = normalizeCurrencyKey(currencyKey);
    const value = gateway?.discountAmounts?.[normalizedCurrencyKey]
        ?? gateway?.discountAmounts?.[normalizedCurrencyKey.toUpperCase()]
        ?? gateway?.[`discountAmount${normalizedCurrencyKey.charAt(0).toUpperCase()}${normalizedCurrencyKey.slice(1)}`]
        ?? gateway?.[`discount_amount_${normalizedCurrencyKey}`];

    if (value !== undefined && value !== null && value !== '') {
        return toNumber(value, 0);
    }

    if (normalizedCurrencyKey === 'usd') {
        return toNumber(gateway?.discountAmountYearly, 0);
    }

    return 0;
};

export const isGatewayFree = (gateway = {}) =>
    getGatewayPrice(gateway, 'usd') <= 0 && getGatewayPrice(gateway, 'mmk') <= 0;

export const isPlanFree = (plan = {}) =>
    isGatewayFree(plan?.gateways?.monthly) && isGatewayFree(plan?.gateways?.yearly);

export const getGatewayPrimaryPrice = (gateway = {}) => {
    const usd = getGatewayPrice(gateway, 'usd');
    const mmk = getGatewayPrice(gateway, 'mmk');
    return usd > 0 ? usd : mmk;
};

export const formatPlanCurrencyAmount = (amount, currencyKey = 'usd') => {
    const normalizedCurrencyKey = normalizeCurrencyKey(currencyKey);
    const value = toNumber(amount, 0);
    const formatter = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: normalizedCurrencyKey === 'usd' ? 2 : 0,
        minimumFractionDigits: 0,
    });

    if (normalizedCurrencyKey === 'mmk') {
        return `${formatter.format(value)} Ks`;
    }

    return `$${formatter.format(value)}`;
};

export const getGatewayPriceDisplaySettings = (gateway = {}) => (
    normalizePriceDisplaySettings(gateway, DEFAULT_GATEWAYS.monthly)
);

export const getGatewayPriceDisplayParts = (gateway = {}) => {
    const settings = getGatewayPriceDisplaySettings(gateway);
    const availablePrices = PLAN_PRICE_CURRENCIES
        .map((currency) => ({
            ...currency,
            amount: getGatewayPrice(gateway, currency.key),
        }))
        .filter((currency) => currency.amount > 0);

    if (availablePrices.length === 0) {
        return {
            primary: { key: 'usd', code: 'USD', label: 'USD', symbol: '$', amount: 0 },
            secondary: null,
            settings,
            isFree: true,
        };
    }

    const primary = availablePrices.find((currency) => currency.key === settings.primaryCurrency)
        || availablePrices[0];
    const secondary = settings.showSecondary
        ? availablePrices.find((currency) => currency.key !== primary.key) || null
        : null;

    return {
        primary,
        secondary,
        settings,
        isFree: false,
    };
};

export const getGatewayDisplayCurrencyOrder = (gateway = {}, { includeHidden = false } = {}) => {
    const displayParts = getGatewayPriceDisplayParts(gateway);
    const orderedKeys = [
        displayParts.primary?.key,
        displayParts.secondary?.key,
        ...(includeHidden ? PLAN_PRICE_CURRENCIES.map((currency) => currency.key) : []),
    ].filter(Boolean);

    return [...new Set(orderedKeys)];
};

export const formatGatewayPrices = (gateway = {}) => {
    const displayParts = getGatewayPriceDisplayParts(gateway);
    const parts = [displayParts.primary, displayParts.secondary]
        .filter((part) => part && part.amount > 0)
        .map((part) => formatPlanCurrencyAmount(part.amount, part.key));

    return parts.length > 0 ? parts.join(' / ') : '$0';
};

export const serializePlanPayload = (planForm = {}) => {
    const normalized = normalizePlanRecord(planForm);

    return {
        name: normalized.name || '',
        description: normalized.description || null,
        currency: 'USD',
        permissions: normalized.permissions || {},
        display_order: Number(normalized.displayOrder ?? normalized.display_order ?? 0) || 0,
        is_popular: Boolean(normalized.isPopular),
        gateways: normalized.gateways,
        price: normalized.gateways.monthly.price,
        updated_at: new Date().toISOString(),
    };
};
